import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FileText, Download, Calendar, Filter, RefreshCw, TrendingUp, Package,
  BarChart2, Users, MapPin, CheckCircle2, AlertTriangle, Clock, DollarSign
} from 'lucide-react';
import { WarehousePDFGenerator } from '../../utils/warehousePDFGenerator';

const WarehouseReports = () => {
  const [selectedReport, setSelectedReport] = useState(null);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');

  // Industry-standard report types
  const reportTypes = [
    {
      id: 'inventory',
      name: 'Inventory Valuation Report',
      description: 'Complete inventory listing with values, quantities, and locations',
      icon: Package,
      category: 'Inventory',
      frequency: 'Daily, Weekly, Monthly'
    },
    {
      id: 'abc',
      name: 'ABC Analysis Report',
      description: 'Categorize inventory by value (A=High, B=Medium, C=Low)',
      icon: TrendingUp,
      category: 'Inventory',
      frequency: 'Monthly'
    },
    {
      id: 'cycle-count',
      name: 'Cycle Count Report',
      description: 'Detailed cycle counting results with variances',
      icon: CheckCircle2,
      category: 'Inventory',
      frequency: 'As Needed'
    },
    {
      id: 'operations',
      name: 'Operations Summary Report',
      description: 'Receiving, shipping, and put-away operations summary',
      icon: BarChart2,
      category: 'Operations',
      frequency: 'Daily, Weekly'
    },
    {
      id: 'performance',
      name: 'Performance Metrics Report',
      description: 'Throughput, accuracy, utilization, and cost metrics',
      icon: TrendingUp,
      category: 'Performance',
      frequency: 'Weekly, Monthly'
    },
    {
      id: 'location',
      name: 'Location Utilization Report',
      description: 'Zone, aisle, rack, shelf, and bin utilization analysis',
      icon: MapPin,
      category: 'Location',
      frequency: 'Monthly'
    },
    {
      id: 'picking',
      name: 'Picking Performance Report',
      description: 'Picking methods comparison and worker performance',
      icon: Users,
      category: 'Operations',
      frequency: 'Weekly'
    },
    {
      id: 'putaway',
      name: 'Put-away Performance Report',
      description: 'Put-away efficiency and location optimization',
      icon: Package,
      category: 'Operations',
      frequency: 'Weekly'
    },
    {
      id: 'returns',
      name: 'Returns Analysis Report',
      description: 'Return reasons, trends, and impact analysis',
      icon: AlertTriangle,
      category: 'Operations',
      frequency: 'Monthly'
    },
    {
      id: 'cost',
      name: 'Cost Analysis Report',
      description: 'Cost per order, labor costs, and operational expenses',
      icon: DollarSign,
      category: 'Financial',
      frequency: 'Monthly'
    },
  ];

  const handleGenerateReport = async (reportId) => {
    try {
      const reportType = reportTypes.find(r => r.id === reportId);
      if (!reportType) return;

      // Mock data based on report type
      let reportData = {};
      const reportIdNum = `RPT-${Date.now()}`;

      switch (reportId) {
        case 'inventory':
          reportData = {
            reportId: reportIdNum,
            warehouse: selectedWarehouse === 'all' ? 'All Warehouses' : `Warehouse ${selectedWarehouse}`,
            summary: {
              totalItems: 5340,
              totalValue: 3025000,
              categories: 12,
              lowStock: 45
            },
            items: [
              { sku: 'ELEC-001', name: 'Laptop Dell XPS 15', category: 'Electronics', quantity: 125, location: 'Zone A → Aisle A1', value: 125000 },
              { sku: 'FURN-045', name: 'Office Chair Ergonomic', category: 'Furniture', quantity: 89, location: 'Zone B → Aisle B1', value: 44500 },
              { sku: 'APPL-089', name: 'Air Conditioner 2 Ton', category: 'Appliances', quantity: 45, location: 'Zone C → Aisle C1', value: 67500 },
              { sku: 'OFF-123', name: 'Printer HP LaserJet', category: 'Office Supplies', quantity: 67, location: 'Zone A → Aisle A2', value: 33500 },
              { sku: 'RAW-234', name: 'Steel Sheets 10mm', category: 'Raw Materials', quantity: 1200, location: 'Zone D → Aisle D1', value: 240000 },
            ]
          };
          const inventoryBlob = await WarehousePDFGenerator.generateInventoryReport(reportData);
          downloadBlob(inventoryBlob, `inventory-report-${reportIdNum}.pdf`);
          break;

        case 'abc':
          reportData = {
            reportId: reportIdNum,
            summary: {
              categoryA: { items: 150, value: 1800000, percentage: 60 },
              categoryB: { items: 450, value: 900000, percentage: 30 },
              categoryC: { items: 4740, value: 325000, percentage: 10 }
            },
            categoryA: [
              { sku: 'ELEC-001', name: 'Laptop Dell XPS 15', quantity: 125, unitValue: 1000, totalValue: 125000 },
              { sku: 'ELEC-002', name: 'Monitor 27" 4K', quantity: 30, unitValue: 800, totalValue: 24000 },
            ]
          };
          const abcBlob = await WarehousePDFGenerator.generateABCAnalysisReport(reportData);
          downloadBlob(abcBlob, `abc-analysis-${reportIdNum}.pdf`);
          break;

        case 'cycle-count':
          reportData = {
            reportId: reportIdNum,
            date: new Date().toLocaleDateString(),
            assignedTo: 'John Doe',
            summary: {
              totalItems: 250,
              matched: 245,
              variances: 5,
              accuracy: 98.0
            },
            variances: [
              { sku: 'ELEC-001', name: 'Laptop Dell XPS', expected: 125, counted: 123, variance: -2, location: 'Zone A → Aisle A1' },
              { sku: 'OFF-123', name: 'Printer HP', expected: 67, counted: 65, variance: -2, location: 'Zone A → Aisle A2' },
            ]
          };
          const cycleBlob = await WarehousePDFGenerator.generateCycleCountReport(reportData);
          downloadBlob(cycleBlob, `cycle-count-${reportIdNum}.pdf`);
          break;

        case 'operations':
          reportData = {
            reportId: reportIdNum,
            period: `${dateRange.start || '2024-01-01'} to ${dateRange.end || '2024-01-31'}`,
            warehouse: selectedWarehouse === 'all' ? 'All Warehouses' : `Warehouse ${selectedWarehouse}`,
            receiving: [
              { po: 'PO-2024-089', supplier: 'Tech Solutions Ltd', items: 45, status: 'Completed', date: '2024-01-28' },
              { po: 'PO-2024-092', supplier: 'Office Supplies Co', items: 120, status: 'In Progress', date: '2024-01-28' },
            ],
            shipping: [
              { order: 'ORD-2024-156', customer: 'ABC Corporation', items: 12, status: 'Shipped', dueDate: '2024-01-28' },
              { order: 'ORD-2024-157', customer: 'XYZ Industries', items: 8, status: 'Packing', dueDate: '2024-01-28' },
            ]
          };
          const opsBlob = await WarehousePDFGenerator.generateOperationsReport(reportData);
          downloadBlob(opsBlob, `operations-report-${reportIdNum}.pdf`);
          break;

        case 'performance':
          reportData = {
            reportId: reportIdNum,
            period: `${dateRange.start || '2024-01-01'} to ${dateRange.end || '2024-01-31'}`,
            metrics: {
              throughput: 19500,
              accuracy: 99.3,
              utilization: 77,
              costPerOrder: 10.5,
              cycleTime: 1.8
            },
            workers: [
              { name: 'John Doe', role: 'Picker', pickRate: 145, accuracy: 99.2, tasksCompleted: 23, efficiency: 92.5 },
              { name: 'Jane Smith', role: 'Picker', pickRate: 132, accuracy: 98.8, tasksCompleted: 20, efficiency: 88.3 },
            ]
          };
          const perfBlob = await WarehousePDFGenerator.generatePerformanceReport(reportData);
          downloadBlob(perfBlob, `performance-report-${reportIdNum}.pdf`);
          break;

        case 'location':
          reportData = {
            reportId: reportIdNum,
            warehouse: selectedWarehouse === 'all' ? 'All Warehouses' : `Warehouse ${selectedWarehouse}`,
            zones: [
              { name: 'Zone A', aisles: 3, racks: 7, shelves: 20, bins: 50, utilization: 85 },
              { name: 'Zone B', aisles: 2, racks: 3, shelves: 7, bins: 12, utilization: 72 },
              { name: 'Zone C', aisles: 2, racks: 3, shelves: 6, bins: 10, utilization: 78 },
            ],
            bins: [
              { id: 'A1-R1-S1-B1', location: 'Zone A → Aisle A1 → Rack A1-R1 → Shelf A1-R1-S1', item: 'Laptop Dell XPS', capacity: 100, used: 75, status: 'Occupied' },
              { id: 'A1-R1-S1-B2', location: 'Zone A → Aisle A1 → Rack A1-R1 → Shelf A1-R1-S1', item: 'Monitor 27"', capacity: 100, used: 45, status: 'Partial' },
            ]
          };
          const locBlob = await WarehousePDFGenerator.generateLocationReport(reportData);
          downloadBlob(locBlob, `location-report-${reportIdNum}.pdf`);
          break;

        default:
          alert(`Report generation for ${reportType.name} is coming soon!`);
          return;
      }

      alert(`Report "${reportType.name}" generated successfully!`);
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Error generating report. Please try again.');
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const categories = [...new Set(reportTypes.map(r => r.category))];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Warehouse Reports</h2>
            <p className="text-sm text-gray-600 mt-1">Generate industry-standard warehouse reports</p>
          </div>
          <div className="flex gap-3">
            <select
              value={selectedWarehouse}
              onChange={(e) => setSelectedWarehouse(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">All Warehouses</option>
              <option value="1">Main Warehouse</option>
              <option value="2">Distribution Center A</option>
              <option value="3">Cold Storage</option>
              <option value="4">Express Hub</option>
            </select>
            <div className="flex gap-2">
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Start Date"
              />
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="End Date"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Report Categories */}
      {categories.map((category) => (
        <div key={category} className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">{category} Reports</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reportTypes
              .filter(report => report.category === category)
              .map((report) => {
                const Icon = report.icon;
                return (
                  <motion.div
                    key={report.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className={`p-3 rounded-lg bg-orange-100`}>
                        <Icon className="w-6 h-6 text-orange-600" />
                      </div>
                      <span className="text-xs text-gray-500">{report.frequency}</span>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">{report.name}</h4>
                    <p className="text-sm text-gray-600 mb-4">{report.description}</p>
                    <button
                      onClick={() => handleGenerateReport(report.id)}
                      className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center justify-center gap-2 text-sm font-medium"
                    >
                      <Download className="w-4 h-4" />
                      Generate Report
                    </button>
                  </motion.div>
                );
              })}
          </div>
        </div>
      ))}

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => {
              setDateRange({ start: '', end: '' });
              setSelectedWarehouse('all');
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            Reset Filters
          </button>
          <button
            onClick={() => {
              const today = new Date();
              const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
              setDateRange({
                start: lastMonth.toISOString().split('T')[0],
                end: today.toISOString().split('T')[0]
              });
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm"
          >
            <Calendar className="w-4 h-4" />
            Last Month
          </button>
          <button
            onClick={() => {
              const today = new Date();
              const lastWeek = new Date(today);
              lastWeek.setDate(today.getDate() - 7);
              setDateRange({
                start: lastWeek.toISOString().split('T')[0],
                end: today.toISOString().split('T')[0]
              });
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm"
          >
            <Calendar className="w-4 h-4" />
            Last Week
          </button>
        </div>
      </div>
    </div>
  );
};

export default WarehouseReports;
